/**
 * Default due dates (Roadmap 1849, also 1788 / 1790).
 * - Work type: "Default due date: N days after start" (backend Service.noOfDays).
 * - Subtask template row: "Due: N days after the work's start" or "N days before
 *   the work's due date" (backend WorkTemplateTask.noOfDays + isAfterStartDate).
 * Empty = no default. Allowed: whole numbers 0..365 (same rule as the server).
 */

export const DUE_DAYS_MIN = 0;
export const DUE_DAYS_MAX = 365;
export const DUE_DAYS_ERROR = "Use a whole number of days from 0 to 365.";

export type ParsedDueDays =
  | { ok: true; value: number | null }
  | { ok: false; error: string };

/** Text box value -> number of days (null = empty = no default). */
export const parseDueDays = (
  input: string | null | undefined,
): ParsedDueDays => {
  const text = (input ?? "").trim();

  if (!text) {
    return { ok: true, value: null };
  }

  if (!/^\d+$/.test(text)) {
    return { ok: false, error: DUE_DAYS_ERROR };
  }

  const value = Number(text);

  if (value < DUE_DAYS_MIN || value > DUE_DAYS_MAX) {
    return { ok: false, error: DUE_DAYS_ERROR };
  }

  return { ok: true, value };
};

/** Saved value -> text box value ("" = no default). */
export const dueDaysToInput = (value: number | null | undefined) =>
  value === null || value === undefined ? "" : String(value);

/** A usable saved value (a whole number within 0..365), else null. */
export const validDueDays = (value: number | null | undefined) =>
  typeof value === "number" &&
  Number.isInteger(value) &&
  value >= DUE_DAYS_MIN &&
  value <= DUE_DAYS_MAX
    ? value
    : null;

/** Same time of day, N calendar days later (local time, DST safe). */
export const addDays = (date: Date, days: number) => {
  const result = new Date(date.getTime());

  result.setDate(result.getDate() + days);
  return result;
};

/** Start date + the work type's default days, or null when it has none. */
export const defaultDueDateFromStart = (
  start: Date | null | undefined,
  days: number | null | undefined,
) => {
  const n = validDueDays(days);

  if (!start || n === null || Number.isNaN(start.getTime())) {
    return null;
  }

  return addDays(start, n);
};

/** "10 days after start" / "1 day before due" / "" (none). */
export const dueDaysLabel = (
  days: number | null | undefined,
  afterStart: boolean,
) => {
  const n = validDueDays(days);

  if (n === null) {
    return "";
  }

  return `${n} day${n === 1 ? "" : "s"} ${
    afterStart ? "after start" : "before due"
  }`;
};
